<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template v-if="!isNewBottomNavVariant" #leftAction>
          <FreeBackButton @click="router.push({ name: 'MainPage' })" />
        </template>
        <template #default>
          <h2 class="text-center text-lg font-semibold">
            {{ $t('favoritesPage.title') }}
          </h2>
        </template>
      </FreeHeader>
      <div class="px-4 pb-4">
        <FreeSegmentControl :active-id="FlowType.AVIA">
          <FreeSegment :id="FlowType.AVIA" :name="FlowType.AVIA">
            {{ $t('favoritesPage.tabAvia') }}
          </FreeSegment>
          <FreeSegment
            :id="FlowType.TOURS"
            :name="FlowType.TOURS"
            @onChange="appNavLinks.openTours('favorites')"
          >
            {{ $t('favoritesPage.tabTours') }}
          </FreeSegment>
        </FreeSegmentControl>
      </div>
    </template>
    <template #default>
      <div :class="['space-y-4', isNewBottomNavVariant ? 'pb-20' : 'pb-4']">
        <FavoritesListShimmer
          v-if="
            userStore.isAuthorizationLoading ||
            (isAuthorized && (isLoading || isFavoritesExperimentLoading))
          "
        />
        <FavoritesListView
          v-else-if="
            isAuthorized && !isDirectionCards && activeItems.length > 0
          "
          :groups="groupedByDirection"
          :dict="watchlistDict"
          @remove="removeFavorite"
          @openItem="handleOpenItem"
        />
        <FavoritesCardsView
          v-else-if="
            isAuthorized && isDirectionCards && directionCards.length > 0
          "
          :cards="directionCards"
          :dict="watchlistDict"
          @openDirection="openDirection"
        />
        <FavoritesEmptyState
          v-else
          @findTickets="router.push({ name: 'MainPage' })"
        />
      </div>
      <BottomNav v-if="isNewBottomNavVariant" />
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, watch } from 'vue';
  import { useRouter, useRoute } from 'vue-router';
  import {
    FreeBackButton,
    FreeHeader,
    FreePageLayout,
    FreeSegmentControl,
    FreeSegment,
    FlowType,
    appNavLinks,
    amplitudeExperiment,
  } from 'freedom-shared';

  import { BottomNav } from '@/widgets/bottom-nav';
  import {
    FavoritesListView,
    FavoritesEmptyState,
    FavoritesListShimmer,
  } from '@/widgets/favorites';

  import {
    useWatchlist,
    groupByDirection,
    toDirectionCards,
    navigateFromWatchlistItem,
    type WatchlistItem,
  } from '@/features/favorites';

  import { amplitudeEventsLib } from '@/shared/lib';
  import { useUserStore } from '@/shared/model';

  import { FavoritesCardsView } from './ui';

  const router = useRouter();
  const route = useRoute();
  const userStore = useUserStore();
  const {
    items,
    isLoading,
    toggleSearch,
    toggleOffer,
    watchlistDict,
    isAuthorized,
  } = useWatchlist();

  const activeItems = computed(() =>
    items.value.filter(item => item.active !== false),
  );

  const groupedByDirection = computed(() =>
    groupByDirection(activeItems.value),
  );
  const directionCards = computed(() =>
    toDirectionCards(groupedByDirection.value),
  );

  const { isVariant: isNewBottomNavVariant } =
    amplitudeExperiment.lib.checkExperimentVariant(
      amplitudeExperiment.config.flags.NEW_BOTTOM_NAV,
      'bottom_nav_bar',
    );

  const {
    isVariant: isDirectionCards,
    isExperimentLoading: isFavoritesExperimentLoading,
  } = amplitudeExperiment.lib.checkExperimentVariant(
    amplitudeExperiment.config.flags.AVIA_FAVORITES_PAGE,
    'direction_cards',
  );

  const favoritesVariant = computed<'list_view' | 'direction_cards'>(() =>
    isDirectionCards.value ? 'direction_cards' : 'list_view',
  );

  const favoritesSource = computed(() => {
    const { source } = route.query;
    if (source === 'tabbar' || source === 'main_page') {
      return source;
    }
    return 'tabbar';
  });

  let pageOpenEventFired = false;

  watch(
    [isLoading, isDirectionCards, isAuthorized, isFavoritesExperimentLoading],
    ([loading, _dirCards, authorized, experimentLoading]) => {
      if (experimentLoading || (authorized && loading) || pageOpenEventFired) {
        return;
      }
      pageOpenEventFired = true;

      amplitudeEventsLib.favoritesPageOpen({
        variant: favoritesVariant.value,
        source: favoritesSource.value,
        search_count: activeItems.value.filter(i => i.hash === null).length,
        offer_count: activeItems.value.filter(i => i.hash !== null).length,
      });
    },
    { immediate: true },
  );

  function removeFavorite(item: (typeof activeItems.value)[number]) {
    if (item.hash) {
      toggleOffer(
        item.hash,
        item.search_query,
        item.initial_price,
        item.currency,
      );
      return;
    }

    toggleSearch(item.search_query, item.initial_price, item.currency);
  }

  function openDirection(routeKey: string) {
    router.push({ name: 'FavoritesDirectionPage', params: { routeKey } });
  }

  const displayOrderItems = computed(() =>
    groupedByDirection.value.flatMap(group => [
      ...group.savedSearches,
      ...group.savedTickets,
    ]),
  );

  function handleOpenItem(item: WatchlistItem) {
    const position = displayOrderItems.value.findIndex(i => i.id === item.id);
    if (item.hash !== null) {
      amplitudeEventsLib.favoritesOfferClick(position, favoritesVariant.value);
    } else {
      amplitudeEventsLib.favoritesSearchClick(position, favoritesVariant.value);
    }
    navigateFromWatchlistItem(router, item);
  }
</script>
